import type {
  PaymentFlow,
  PaymentPlanBatchPaymentsItem,
  User,
} from 'payments-api/model';
import { usePostPaymentFlowsSearchHook } from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { NavTriangleIcon } from 'components/SideNav/PaymentsNav';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';

import {
  DirectionArrowCells,
  DirectionArrowHeaderCells,
} from './DirectionArrowCells';
import { FormatBillingAmount } from './formatBillingAmount';
import {
  kInvoiceTabCellPadding,
  kInvoiceTabHeaderText,
  UsersHeaderCell,
} from './invoiceTabLayout';
import { UserLabelCell } from './UserLabelCell';

/** Hardcoded until the API surfaces the payment method per payment. */
const kMethodLabel = 'ACH';

type StatusToken = {
  label: string;
  /** Fraction of the progress bar filled, 0–1. */
  progress: number;
  /** Tailwind background colour class for the filled portion. */
};

const paymentStatusLabel = (status: string): string => {
  switch (status) {
    case 'settled':
      return 'Settled';
    case 'initiated':
    case 'pending':
      return 'Initiated';
    case 'returned':
      return 'Returned';
    default:
      return 'Not yet initiated';
  }
};

const flowStatusToken = (flow: PaymentFlow): StatusToken => {
  const payments = flow.payment_plan.batches.flatMap((b) => b.payments);
  if (payments.length === 0) {
    // Fallback to the flow's own status when the plan carries no payments.
    return {
      label: paymentStatusLabel(flow.status),
      progress: flow.status === 'settled' ? 1 : 0,
    };
  }
  const counts = payments.reduce(
    (acc, p) => {
      acc.total += 1;
      if (p.status === 'settled') acc.settled += 1;
      else if (p.status === 'returned') acc.returned += 1;
      else if (p.status === 'initiated' || p.status === 'pending')
        acc.initiated += 1;
      return acc;
    },
    { total: 0, settled: 0, returned: 0, initiated: 0 }
  );
  const progress = counts.settled / counts.total;
  if (counts.settled === counts.total) {
    return { label: 'Settled', progress: 1 };
  }
  if (counts.returned > 0) {
    return { label: 'Returned', progress: 1 };
  }
  if (counts.initiated > 0 || counts.settled > 0) {
    return {
      label: 'Initiated',
      progress: Math.max(progress, 0.1),
    };
  }
  return {
    label: 'Not yet initiated',
    progress: 0,
  };
};

const StatusIndicator = ({ flow }: { flow: PaymentFlow }) => {
  const token = flowStatusToken(flow);
  return (
    <div className="flex items-center gap-x-f1">
      <span className="text-xmd text-main-500">{token.label}</span>
      <div className="w-[128px] h-[9px] bg-main-200 overflow-hidden">
        <div
          className={clsx('h-full', 'bg-gradient-to-r from-main-500 to-main')}
          style={{ width: `${token.progress * 100}%` }}
        />
      </div>
    </div>
  );
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toDateString().split(' ').slice(1).join(' ');
  } catch {
    return '—';
  }
};

const RowLayout = ({
  UserField,
  DirectionField,
  AmountField,
  DateField,
  StatusField,
  MethodField,
  bottomBorder = false,
}: {
  UserField: React.ReactNode;
  DirectionField: React.ReactNode;
  AmountField: React.ReactNode;
  DateField: React.ReactNode;
  StatusField: React.ReactNode;
  MethodField: React.ReactNode;
  bottomBorder?: boolean;
}) => {
  const cellClass = 'flex items-center justify-end w-[20%] text-xmd pr-f1 ';
  return (
    <div
      className={clsx(
        'flex flex-row w-full items-center',
        bottomBorder && 'border-b-[0.5px] border-main-200'
      )}
    >
      <div className="flex w-1/2">{UserField}</div>
      <div className="flex w-1/2 justify-between">
        <div
          className={clsx(
            'w-fit',
            !bottomBorder && 'border-l-[0.5px] border-main-200'
          )}
        >
          {DirectionField}
        </div>
        <div className={cellClass}>{AmountField}</div>
        <div className={cellClass}>{DateField}</div>
        <div className={cellClass}>{StatusField}</div>
        <div className={cellClass}>{MethodField}</div>
      </div>
    </div>
  );
};

const FlowPaymentRow = ({
  payment,
  users,
  created,
}: {
  payment: PaymentPlanBatchPaymentsItem;
  users: User[];
  created: string;
}) => {
  const externalId = payment.user.external_id;
  const user = users.find((u) => u.external_id === externalId);
  const precision = currencyPrecisionMap[payment.currency as CurrencyCode];
  return (
    <RowLayout
      UserField={
        <UserLabelCell
          externalId={externalId}
          role={user?.role}
          withBorder={false}
        />
      }
      DirectionField={
        <DirectionArrowCells
          direction={payment.direction as 'payin' | 'payout'}
          withBorder
        />
      }
      AmountField={
        <FormatBillingAmount
          amount={Number(payment.amount)}
          currencyCode={payment.currency as CurrencyCode}
          currencyPrecision={precision}
        />
      }
      DateField={formatDate(created)}
      StatusField={paymentStatusLabel(payment.status)}
      MethodField={kMethodLabel}
    />
  );
};

const FlowRow = ({
  flow,
  isExpanded,
  onToggle,
  users,
}: {
  flow: PaymentFlow;
  isExpanded: boolean;
  onToggle: () => void;
  users: User[];
}) => {
  const payments = flow.payment_plan.batches.flatMap((b) => b.payments);
  const totalAmountPerCurrency = payments
    .filter((p) => p.direction === 'payin')
    .reduce((acc, p) => {
      acc[p.currency as CurrencyCode] =
        (acc[p.currency as CurrencyCode] || 0) + Number(p.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);
  return (
    <div
      className="flex flex-col w-full border-[0.5px] border-main-200 border-t-0"
      data-testid={`flow-row-${flow.id}`}
    >
      <button
        type="button"
        className={clsx(
          'flex flex-row w-full items-center h-f4  hover:bg-negative',
          kInvoiceTabCellPadding
        )}
        onClick={onToggle}
      >
        <div className="flex flex-row items-center justify-between w-1/2">
          <span className="flex items-center gap-x-f1 text-xmd text-main truncate text-left w-fit">
            <NavTriangleIcon active={false} hovered={false} />
            {new Date(flow.created).toDateString().substring(4)}
          </span>
          <div className="text-xmd text-main flex flex-row items-center justify-end w-fit">
            {Object.entries(totalAmountPerCurrency).map(
              ([currency, amount]) => (
                <FormatBillingAmount
                  key={currency}
                  amount={amount}
                  currencyCode={currency as CurrencyCode}
                  currencyPrecision={
                    currencyPrecisionMap[currency as CurrencyCode]
                  }
                />
              )
            )}
          </div>
        </div>

        <div className="flex flex-row items-center gap-x-f1 w-1/2 justify-end">
          <StatusIndicator flow={flow} />
          <Icon
            type={isExpanded ? 'up' : 'down'}
            size="sm"
            primaryColor="gray"
          />
        </div>
      </button>
      {isExpanded && payments.length > 0 && (
        <div className="flex flex-col w-full pt-f2">
          <RowLayout
            UserField={<UsersHeaderCell />}
            DirectionField={<DirectionArrowHeaderCells withBorder={false} />}
            AmountField={<span className={kInvoiceTabHeaderText}>Amount</span>}
            DateField={<span className={kInvoiceTabHeaderText}>Date</span>}
            StatusField={<span className={kInvoiceTabHeaderText}>Status</span>}
            MethodField={<span className={kInvoiceTabHeaderText}>Method</span>}
            bottomBorder
          />
          {payments.map((p) => (
            <FlowPaymentRow
              key={p.payment_id}
              payment={p}
              users={users}
              created={flow.created}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type Props = {
  invoiceId: string;
  users: User[];
};

export const PaymentFlowsTable = ({ invoiceId, users }: Props): JSX.Element => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const searchPaymentFlows = usePostPaymentFlowsSearchHook();
  const { data, isLoading } = useQuery({
    queryKey: ['/payment-flows', { invoice_id: invoiceId }] as const,
    queryFn: ({ signal }) =>
      searchPaymentFlows({ invoice_id: invoiceId }, signal),
  });

  const rawData = data?.data ?? [];
  const flows = rawData.sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-f8 text-xmd text-main-500">
        Loading payment flows...
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="flex items-center justify-center h-f8 text-xmd text-main-500">
        No payment flows yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full " data-testid="payment-flows-table">
      {flows.map((flow) => (
        <FlowRow
          key={flow.id}
          flow={flow}
          isExpanded={expandedId === flow.id}
          onToggle={() =>
            setExpandedId((prev) => (prev === flow.id ? null : flow.id))
          }
          users={users}
        />
      ))}
    </div>
  );
};
